import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// A signed email link grants access to one order, without creating an account.
try {
  const link = new URL(window.location.href);
  const order = link.pathname.match(/^\/paiement\/(\d+)$/);
  const access = link.searchParams.get('order_token');
  if (order && access && access.length < 4096) {
    sessionStorage.setItem(`order-access-${order[1]}`, access);
    link.searchParams.delete('order_token');
    window.history.replaceState(window.history.state, '', link.pathname + link.search + link.hash);
  }
} catch { /* Storage may be disabled by the browser. */ }

// Attach credentials only to this site's API, never to external address services.
axios.interceptors.request.use(config => {
  try {
    const url = new URL(config.url, window.location.origin);
    const api = new URL(API, window.location.origin);
    if (url.origin === api.origin && url.pathname.startsWith(api.pathname + '/')) {
      let credential = localStorage.getItem('token');
      const match = url.pathname.match(/\/devis\/(\d+)(?:\/|$)/);
      const orderId = match?.[1] || url.searchParams.get('devis_id');
      if (!credential && orderId) credential = sessionStorage.getItem(`order-access-${orderId}`);
      if (credential) { config.headers = config.headers || {}; config.headers.Authorization = `Bearer ${credential}`; }
    }
  } catch { /* Requests without a valid URL keep their existing configuration. */ }
  return config;
});
axios.interceptors.response.use(response => {
  const order = response.data;
  if (order?.id && order?.order_access_token) {
    try { sessionStorage.setItem(`order-access-${order.id}`, order.order_access_token); } catch { /* Browser storage unavailable. */ }
  }
  return response;
});

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        params: { token }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    setToken(access_token);
    setUser(newUser);
    localStorage.setItem('token', access_token);
    return response.data;
  };

  const login = async (credentials) => {
    const response = await axios.post(`${API}/auth/login`, credentials);
    const { access_token, user: loggedUser } = response.data;
    setToken(access_token);
    setUser(loggedUser);
    localStorage.setItem('token', access_token);
    return response.data;
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, register, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
